// @flow

import { useRef } from 'react';
import { Panel, PanelBar, PanelButtonBar, PanelHeader } from '../../../element/Panel';
import { Dialog, useDialog } from '../../../element/Dialog';
import { EmailInput, useEmailValidation } from '../../../element/EmailInput';
import { Notice } from '../../../element/Notice';

import type { EditorModal } from '../../../common/Editor';
import type { BbAccountParams, BbAccount } from '../../../../api/type.acc';

type BillingEmailTransition = 'add' | 'update' | 'remove' | 'none';

type OriginalBillingEmailState = {
    initialised: boolean,
    value: ?string,
};

type UpdateBillingEmailDialogData = {
    previousEmail: string,
    newEmail: string,
};

const normaliseBillingEmail = (billingEmail: ?string): ?string => {
    if (billingEmail == null || billingEmail === '') {
        return null;
    }

    return billingEmail;
};

const getBillingEmailTransition = (originalBillingEmail: ?string, currentBillingEmail: ?string): BillingEmailTransition => {
    if (originalBillingEmail === currentBillingEmail) {
        return 'none';
    }

    if (originalBillingEmail == null && currentBillingEmail != null) {
        return 'add';
    }

    if (originalBillingEmail != null && currentBillingEmail == null) {
        return 'remove';
    }

    if (originalBillingEmail != null && currentBillingEmail != null) {
        return 'update';
    }

    return 'none';
};

type AccountBillingEmailEditProps = {
    +editor: EditorModal<BbAccountParams, null, BbAccount>
}

export const AccountBillingEmailEdit = ({ editor }: AccountBillingEmailEditProps): React$Node => {
    const { value: editAccount, setValue: setEditAccount } = editor;
    const originalBillingEmail = useRef<OriginalBillingEmailState>({ initialised: false, value: null });
    const emailValidation = useEmailValidation();

    const addDialog = useDialog<?string>([
        {
            label: 'Add Billing Email',
            kind: 'primary',
            onSelect: () => editor.onSave(),
        }
    ]);
    const updateDialog = useDialog<UpdateBillingEmailDialogData>([
        {
            label: 'Change Billing Email',
            kind: 'primary',
            onSelect: () => editor.onSave(),
        }
    ]);
    const removeDialog = useDialog<?string>([
        {
            label: 'Remove Billing Email',
            kind: 'primary',
            color: 'red',
            onSelect: () => editor.onSave(),
        }
    ]);

    if (!editAccount) {
        originalBillingEmail.current = { initialised: false, value: null };
        return null;
    }

    if (!originalBillingEmail.current.initialised) {
        originalBillingEmail.current = {
            initialised: true,
            value: normaliseBillingEmail(editAccount.billing_email),
        };
    }

    const currentBillingEmail = normaliseBillingEmail(editAccount.billing_email);
    const transition = getBillingEmailTransition(originalBillingEmail.current.value, currentBillingEmail);

    const showConfirmationDialog = () => {
        if (currentBillingEmail != null && !emailValidation.validate(currentBillingEmail)) return;
        emailValidation.clear();
        switch (transition) {
        case 'add':
            addDialog.show(currentBillingEmail);
            break;
        case 'update':
            if (originalBillingEmail.current.value != null && currentBillingEmail != null) {
                updateDialog.show({
                    previousEmail: originalBillingEmail.current.value,
                    newEmail: currentBillingEmail,
                });
            }
            break;
        case 'remove':
            removeDialog.show(originalBillingEmail.current.value);
            break;
        default:
            break;
        }
    };

    const removeBillingEmail = () => {
        setEditAccount({ ...editAccount, billing_email: '' });
        removeDialog.show(originalBillingEmail.current.value);
    };

    return (
        <>
            <Dialog
                title='Add Billing Email?'
                dialog={addDialog}
                render={(billingEmail) => (
                    <>
                        <p>Billing emails will be sent to <b>{billingEmail}</b> in addition to the account owner.</p>
                        <p>Are you sure you want to add this billing email address?</p>
                    </>
                )}
            />

            <Dialog
                title='Change Billing Email?'
                dialog={updateDialog}
                render={(dialogData) => (
                    <>
                        <p>Billing emails will be sent to <b>{dialogData?.newEmail}</b> instead of <b>{dialogData?.previousEmail}</b>.</p>
                        <p>Are you sure you want to change the additional billing email address?</p>
                    </>
                )}
            />

            <Dialog
                title='Remove Billing Email?'
                dialog={removeDialog}
                render={(billingEmail) => (
                    <>
                        <p>Billing emails will no longer be sent to <b>{billingEmail}</b>.</p>
                        <p>Are you sure you want to remove the additional billing email address?</p>
                    </>
                )}
            />

            <Panel>
                <PanelHeader
                    title={'Billing Email'}
                    description={'Provide an additional email address to receive copies of billing emails'}
                />

                <PanelBar>
                    <Notice type='info' icon='info-fill'>
                        <p>When set, the billing email address will receive a copy of automated billing emails (invoices, payment reminders etc).</p>
                    </Notice>

                    <EmailInput
                        label='Additional Billing Email'
                        value={editAccount.billing_email || ''}
                        onChange={(billing_email) => { emailValidation.clear(); setEditAccount({ ...editAccount, billing_email }); }}
                        optional={true}
                        errorText={emailValidation.error}
                    />
                </PanelBar>
                <PanelButtonBar
                    cacheStatus={editor.messages?.status}
                    primaryButton={{
                        onClick: editor.status === 'edit' ? showConfirmationDialog : null,
                        disabled: transition === 'none',
                    }}
                    cancel={editor.status === 'edit' ? editor.onCancel : null}
                    leftButton={originalBillingEmail.current.value != null ? {
                        children: 'Remove',
                        kind: 'secondary',
                        color: 'red',
                        onClick: editor.status === 'edit' ? removeBillingEmail : null,
                    } : undefined}
                />
            </Panel>
        </>
    );
}